import React from "react";
import { useParams, useNavigate } from "react-router";
import type { IUser } from "../../../types/iuser";
import type { ScoreBadgeTier } from "@shadowcue/ui";
import useConversationSummary from "../../../hooks/useConversationSummary";
import ConversationChat from "./conversation_chat";
import RegradeModal from "./regrade_modal";
import ExportModal from "./export_modal";
import RenameModal from "./rename_modal";
import {
  PageContainer,
  PageTopbar,
  PageHeader,
  ContentContainer,
  StatCard,
  CardLabel,
  ExpandableText,
  TemplateSectionCard,
  ScoredQuestionRow,
  astryx,
  neutralTheme,
  useDocumentTheme,
  FileDown,
  RefreshCw,
} from "@shadowcue/ui";
import type { StatCardTone } from "@shadowcue/ui";

interface Props {
  currentUser: IUser;
}

// Uniform width for the two stat cards so they read as one set.
const STAT_CARD_WIDTH = 240;

const TIER_TONE: Record<ScoreBadgeTier, StatCardTone> = {
  high: "positive",
  mid: "warning",
  low: "critical",
  muted: "neutral",
};

const ConversationSummary = ({ currentUser }: Props) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const mode = useDocumentTheme();

  const {
    summary,
    loading,
    error,
    regradeBanner,
    setRegradeBanner,
    regradeModalOpen,
    setRegradeModalOpen,
    regradeLoading,
    reportModalOpen,
    reportMarkdown,
    reportLoading,
    renamingConversation,
    setRenamingConversation,
    toggleExclusion,
    submitRegrade,
    openExportModal,
    closeExportModal,
    refreshSummary,
  } = useConversationSummary(id, currentUser.authToken);

  const currentLabel = summary?.conversation.name ?? (error ? "Unavailable" : "Loading…");

  return (
    <>
      <astryx.Theme theme={neutralTheme} mode={mode}>
        <astryx.LayerProvider>
          <PageContainer>
            <PageTopbar
              breadcrumbs={[
                { label: "Conversations", href: "/dashboard/conversations" },
                { label: currentLabel },
              ]}
            />

            <ContentContainer loading={loading}>
              {error || !summary ? (
                <astryx.EmptyState
                  title="We couldn't load this conversation."
                  description="Something went wrong fetching the summary. Try again, or head back to your conversations."
                  actions={
                    <astryx.HStack gap={2}>
                      <astryx.Button
                        label="Try again"
                        onClick={() => window.location.reload()}
                      />
                      <astryx.Button
                        label="Back to conversations"
                        variant="primary"
                        onClick={() => navigate("/dashboard/conversations")}
                      />
                    </astryx.HStack>
                  }
                />
              ) : (
                <>
                  <astryx.VStack gap={6}>
                    {regradeBanner && (
                      <astryx.Banner
                        status={regradeBanner.status}
                        title={regradeBanner.message}
                        isDismissable
                        onDismiss={() => setRegradeBanner(null)}
                      />
                    )}

                    <PageHeader
                      title={summary.conversation.name}
                      description={summary.conversation.description}
                      editTitleLabel="Rename conversation"
                      onEditTitle={() =>
                        setRenamingConversation({
                          id: summary.conversation.id,
                          name: summary.conversation.name,
                          description: summary.conversation.description,
                        })
                      }
                      action={
                        <astryx.HStack gap={2}>
                          <astryx.Button
                            label="Regrade"
                            icon={<astryx.Icon icon={RefreshCw} />}
                            onClick={() => setRegradeModalOpen(true)}
                          />
                          <astryx.Button
                            label="Export"
                            variant="primary"
                            icon={<astryx.Icon icon={FileDown} />}
                            onClick={openExportModal}
                          />
                          <astryx.MoreMenu
                            label="Conversation actions"
                            items={[
                              {
                                label: "Transcript",
                                onClick: () =>
                                  navigate(
                                    `/dashboard/conversations/${summary.conversation.id}/transcript`,
                                  ),
                              },
                              {
                                label: "Rename",
                                onClick: () =>
                                  setRenamingConversation({
                                    id: summary.conversation.id,
                                    name: summary.conversation.name,
                                    description: summary.conversation.description,
                                  }),
                              },
                            ]}
                          />
                        </astryx.HStack>
                      }
                    />

                    <astryx.VStack gap={8}>
                      {/* No wrap: the fill item must shrink on the same line —
                          with wrap, its content-sized flex basis pushes it onto
                          its own row before shrinking kicks in. */}
                      <astryx.HStack gap={4} align="stretch">
                        <astryx.StackItem>
                          <StatCard
                            label="Final Score"
                            width={STAT_CARD_WIDTH}
                            valueAlign="center"
                            tone={
                              summary.finalScorePct != null
                                ? TIER_TONE[summary.finalTier]
                                : "neutral"
                            }
                            value={
                              summary.finalScorePct != null
                                ? `${summary.finalScorePct}%`
                                : "—"
                            }
                            info={
                              summary.finalScorePct != null
                                ? "Average score across this conversation's scored sections."
                                : "No rated sections yet."
                            }
                          />
                        </astryx.StackItem>
                        <astryx.StackItem>
                          <StatCard
                            label="Avg. Template Score"
                            width={STAT_CARD_WIDTH}
                            valueAlign="center"
                            tone={
                              summary.templateScorePct != null
                                ? TIER_TONE[summary.templateScoreTier]
                                : "neutral"
                            }
                            value={
                              summary.templateScorePct != null
                                ? `${summary.templateScorePct}%`
                                : "—"
                            }
                            info="Average final score across all conversations that use this template."
                          />
                        </astryx.StackItem>

                        <astryx.StackItem size="fill">
                          {/* height 100%: match the stretched StackItem so the
                              card bottoms align with the StatCards. */}
                          <astryx.Card variant="default" height="100%">
                            <astryx.VStack gap={2} height="100%">
                              <CardLabel label="AI Summary" />
                              <astryx.StackItem size="fill">
                                <ExpandableText
                                  lines={2}
                                  color={summary.aiSummary ? "primary" : "secondary"}
                                >
                                  {summary.aiSummary ?? "AI summary will be generated once the interview is fully scored."}
                                </ExpandableText>
                              </astryx.StackItem>
                            </astryx.VStack>
                          </astryx.Card>
                        </astryx.StackItem>
                      </astryx.HStack>

                      <astryx.VStack gap={4}>
                        {summary.sectionStats.map((section, idx) => (
                          <TemplateSectionCard
                            key={section.id}
                            index={idx + 1}
                            name={section.name}
                            avgScore={section.sectionPct}
                            maxScore={100}
                            scoreFormat="percent"
                            scoreTier={section.sectionTier}
                          >
                            <astryx.VStack gap={0}>
                              {section.questionStats.map((qs, j) => (
                                <ScoredQuestionRow
                                  key={qs.id}
                                  index={j + 1}
                                  body={qs.body || "Untitled question"}
                                  score={qs.scorePct}
                                  max={100}
                                  scoreFormat="percent"
                                  scoreTier={qs.excluded ? "muted" : qs.tier}
                                  responseLabel="Response summary"
                                  isExcluded={qs.excluded}
                                  idealResponse={
                                    qs.excerpts.length > 0 ? (
                                      <astryx.List listStyle="disc">
                                        {qs.excerpts.map((line, k) => (
                                          <astryx.ListItem
                                            key={k}
                                            label={line.replace(/:$/, "")}
                                          />
                                        ))}
                                      </astryx.List>
                                    ) : (
                                      "No response summary available."
                                    )
                                  }
                                  endAction={
                                    <astryx.MoreMenu
                                      label="Question actions"
                                      variant="ghost"
                                      size="sm"
                                      items={[
                                        {
                                          label: "View in transcript",
                                          onClick: () =>
                                            navigate(
                                              `/dashboard/conversations/${summary.conversation.id}/transcript?focusQuestion=${qs.id}`,
                                            ),
                                        },
                                        ...(currentUser.admin
                                          ? [
                                              {
                                                label: qs.excluded
                                                  ? "Include in score"
                                                  : "Exclude from score",
                                                onClick: () =>
                                                  toggleExclusion(qs.id, !qs.excluded),
                                              },
                                            ]
                                          : []),
                                      ]}
                                    />
                                  }
                                />
                              ))}
                            </astryx.VStack>
                          </TemplateSectionCard>
                        ))}
                      </astryx.VStack>
                    </astryx.VStack>
                  </astryx.VStack>

                  <RegradeModal
                    isOpen={regradeModalOpen}
                    sectionStats={summary.sectionStats}
                    loading={regradeLoading}
                    onRegrade={submitRegrade}
                    onClose={() => setRegradeModalOpen(false)}
                  />

                  <ExportModal
                    isOpen={reportModalOpen}
                    markdown={reportMarkdown}
                    loading={reportLoading}
                    onClose={closeExportModal}
                  />

                  <RenameModal
                    conversation={renamingConversation}
                    authToken={currentUser.authToken}
                    onClose={() => setRenamingConversation(null)}
                    onSaved={refreshSummary}
                  />
                </>
              )}
            </ContentContainer>
          </PageContainer>
        </astryx.LayerProvider>
      </astryx.Theme>
      {summary && (
        <ConversationChat
          conversationId={summary.conversation.id.toString()}
          authToken={currentUser.authToken}
        />
      )}
    </>
  );
};

export default ConversationSummary;
